import { AppSidebar } from "@/components/layout/AppSidebar";
import { Navigation } from "@/components/layout/Navigation";
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { MessageSquare } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '@/hooks';
import { useApp } from '@/contexts/AppProvider';
import { FeedbackDialog } from '@/components/dialogs/FeedbackDialog';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import Logo from "@/components/common/Logo";
import { cn } from '@/lib/utils';
import Footer from "@/components/layout/Footer";

/**
 * The Dashboard component renders the app shell with a persistent sidebar
 * and a lightweight mobile header for the sidebar trigger.
 * The main content area is where the children of the Dashboard component should be rendered.
 */
export default function Dashboard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { isFocusMode } = useApp();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const location = useLocation();
  const isWritePage = location.pathname.startsWith('/app/write');

  if (!user) {
    const showFooter = location.pathname === '/';
    return (
      <div className="min-h-screen w-full flex flex-col">
        <Navigation showAuth className="mt-4" />
        <main className="flex-1">
          {children}
        </main>
        {showFooter && <Footer />}
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full flex">
        {!isWritePage && (
          <AppSidebar onFeedbackClick={() => setFeedbackOpen(true)} />
        )}

        <main className="flex-1 min-h-screen">
          {!isWritePage && (
            <header
              className={cn(
                'md:hidden transition-all duration-500 ease-in-out py-3 px-4 flex items-center justify-between',
                isFocusMode ? 'opacity-0 -translate-y-4 pointer-events-none' : 'opacity-100 translate-y-0'
              )}
            >
              <Link to="/app" className="inline-flex items-center py-2 pl-2 gap-3">
                <Logo className="h-7 w-auto" />
                <span className="sr-only">Arcitext</span>
              </Link>
              <SidebarTrigger />
            </header>
          )}
          {children}

          {/* Feedback Button - Fixed position, non-invasive, hidden on mobile */}
          {!isFocusMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFeedbackOpen(true)}
              className="hidden md:flex fixed bottom-6 right-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback
            </Button>
          )}
        </main>
      </div>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </SidebarProvider >
  )
}
